/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { MessageSquareText, Search, RefreshCw, AlertTriangle, CalendarCheck, CalendarX, Repeat2, Bot, User, ChevronRight, Archive, Mail, Phone } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABEL = { book: 'Prenotazione', cancel: 'Cancellazione', reschedule: 'Spostamento' };
const ACTION_ICON = { book: CalendarCheck, cancel: CalendarX, reschedule: Repeat2 };

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

export default function ConversazioniAI() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [onlyActions, setOnlyActions] = useState(false);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/booking-chat/admin/conversations`, {
        params: { days, only_actions: onlyActions || undefined, only_errors: onlyErrors || undefined, search: search.trim() || undefined },
      });
      setData(res.data);
    } catch (err) {
      logger.error('Conversazioni AI fetch error:', err);
      toast.error('Errore nel caricamento delle conversazioni');
    } finally { setLoading(false); }
  }, [days, onlyActions, onlyErrors, search]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const openDetail = async (conv) => {
    setSelected(conv);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/booking-chat/admin/conversations/${conv.user_id}`, {
        params: { archived_at: conv.archived_at || undefined },
      });
      setDetail(res.data);
    } catch (err) {
      logger.error('Detail error:', err);
      toast.error('Errore nel caricamento della conversazione');
    } finally { setDetailLoading(false); }
  };

  const totals = data?.totals || {};
  const conversations = data?.conversations || [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <PageTitle title="Conversazioni AI" info="Storico delle chat di prenotazione con l'assistente: verifica risposte, azioni eseguite ed errori." />
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="ai-conversations-refresh">
          <RefreshCw size={15} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Conversazioni', value: totals.conversations, icon: MessageSquareText, tid: 'total' },
          { label: 'Con azioni', value: totals.with_actions, icon: CalendarCheck, tid: 'actions' },
          { label: 'Prenotazioni riuscite', value: totals.bookings_ok, icon: CalendarCheck, tid: 'bookings' },
          { label: 'Con errori / fallback', value: totals.with_errors, icon: AlertTriangle, tid: 'errors', warn: true },
        ].map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.tid} data-testid={`ai-conversations-kpi-${k.tid}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{k.label}</p>
                  {loading && !data ? <Skeleton className="h-7 w-10 mt-1" /> : <p className={`text-2xl font-bold ${k.warn && k.value > 0 ? 'text-amber-600' : 'text-[#1F2A37]'}`} style={{fontFamily:'Space Grotesk'}}>{k.value ?? 0}</p>}
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.warn ? 'bg-amber-50 text-amber-600' : 'bg-[#238A92]/10 text-[#238A92]'}`}><Icon size={18} /></div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtri */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca paziente (nome, email) o testo del primo messaggio..." className="pl-9" data-testid="ai-conversations-search" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1">
              {[7, 30, 90].map(d => (
                <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} className={days === d ? 'bg-[#238A92] text-white' : ''} onClick={() => setDays(d)} data-testid={`ai-conversations-days-${d}`}>{d} gg</Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Switch id="only-actions" checked={onlyActions} onCheckedChange={setOnlyActions} data-testid="ai-conversations-only-actions" />
              <Label htmlFor="only-actions" className="text-sm cursor-pointer">Solo con azioni</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="only-errors" checked={onlyErrors} onCheckedChange={setOnlyErrors} data-testid="ai-conversations-only-errors" />
              <Label htmlFor="only-errors" className="text-sm cursor-pointer">Solo con errori</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Elenco */}
      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="p-6 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 rounded" />)}</div>
          ) : conversations.length === 0 ? (
            <div className="p-12 text-center" data-testid="ai-conversations-empty">
              <MessageSquareText size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="font-medium text-[#1F2A37] mb-1" style={{fontFamily:'Space Grotesk'}}>Nessuna conversazione</p>
              <p className="text-sm text-muted-foreground">Nessuna chat negli ultimi {days} giorni con i filtri selezionati.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]">
              {conversations.map(c => {
                const hasProblem = c.errors > 0 || c.fallbacks > 0;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => openDetail(c)}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[hsl(var(--secondary))] transition-colors duration-150"
                      data-testid={`ai-conversation-row-${c.user_id}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${hasProblem ? 'bg-amber-100 text-amber-700' : 'bg-[#238A92]/10 text-[#238A92]'}`}>
                        {hasProblem ? <AlertTriangle size={16} /> : <User size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-[#1F2A37]">{c.patient.nome || c.patient.cognome ? `${c.patient.nome} ${c.patient.cognome}`.trim() : 'Utente eliminato'}</span>
                          {c.patient.email && <span className="text-xs text-muted-foreground">{c.patient.email}</span>}
                          {c.archived_at && <Badge variant="secondary" className="text-[10px] gap-1"><Archive size={10} /> archiviata</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-0.5">{c.preview || <span className="italic">Solo messaggio di benvenuto</span>}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="text-[11px] text-muted-foreground">{c.messages} messaggi · {fmtDateTime(c.started_at)} → {fmtTime(c.last_at)}</span>
                          {c.actions.map((a, i) => {
                            const Icon = ACTION_ICON[a.type] || CalendarCheck;
                            return (
                              <Badge key={i} className={`text-[10px] gap-1 ${a.ok ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}`}>
                                <Icon size={10} /> {ACTION_LABEL[a.type] || a.type} {a.ok ? 'OK' : 'fallita'}
                              </Badge>
                            );
                          })}
                          {c.fallbacks > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100">{c.fallbacks} fallback</Badge>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground flex-shrink-0 mt-2" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Dettaglio */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="ai-conversation-detail">
          <SheetHeader className="p-4 border-b border-[hsl(var(--border))]">
            <SheetTitle style={{fontFamily:'Space Grotesk'}}>
              {detail?.patient?.nome ? `${detail.patient.nome} ${detail.patient.cognome || ''}` : selected ? (selected.patient.nome ? `${selected.patient.nome} ${selected.patient.cognome}` : 'Utente eliminato') : ''}
            </SheetTitle>
            <SheetDescription className="flex flex-wrap gap-3 text-xs">
              {detail?.patient?.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {detail.patient.email}</span>}
              {detail?.patient?.telefono && <span className="inline-flex items-center gap-1"><Phone size={12} /> {detail.patient.telefono}</span>}
              {selected && <span>{fmtDateTime(selected.started_at)}{selected.archived_at ? ' · archiviata' : ' · sessione corrente'}</span>}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[hsl(var(--secondary))]">
            {detailLoading ? (
              [1,2,3,4].map(i => <Skeleton key={i} className={`h-14 rounded-xl ${i % 2 ? 'w-3/4' : 'w-2/3 ml-auto'}`} />)
            ) : (detail?.messages || []).map(m => {
              const isUser = m.role === 'user';
              const act = m.action;
              return (
                <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`ai-msg-${m.role}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${isUser ? 'bg-[#238A92] text-white rounded-br-sm' : 'bg-white text-[#1F2A37] rounded-bl-sm border border-[hsl(var(--border))]'}`}>
                    <div className={`flex items-center gap-1.5 text-[10px] mb-1 ${isUser ? 'text-white/80' : 'text-muted-foreground'}`}>
                      {isUser ? <User size={10} /> : <Bot size={10} />} {isUser ? 'Paziente' : 'Assistente'} · {fmtTime(m.created_at)}
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                    {act && (
                      <div className={`mt-2 rounded-lg px-2.5 py-1.5 text-[11px] font-mono border ${act.ok ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'}`} data-testid="ai-msg-action">
                        <span className="font-semibold font-sans">{ACTION_LABEL[act.type] || act.type} {act.ok ? 'eseguita' : 'FALLITA'}</span>
                        {act.raw && <div className="mt-0.5 break-all">{JSON.stringify(act.raw)}</div>}
                        {act.error && <div className="mt-0.5">{act.error}</div>}
                      </div>
                    )}
                    {m.payment && <p className="mt-1.5 text-[11px] opacity-80">Pagamento richiesto: {Number(m.payment.amount).toFixed(2)} EUR ({m.payment.payment_type})</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
